import { useState } from "react";
import axios from "axios";
import { Mail, Instagram, MapPin, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { CONTACT } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const programOptions = [
  "Skill Development",
  "Shooting Camp",
  "Position Camp",
  "Private Film Study",
  "Travel Team Tryout",
  "General Inquiry",
];

const INSTAGRAM_URL = "https://www.instagram.com/bbatravelbasketball/";
const EMAIL = "bbatravelbasketball@gmail.com";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    player_age: "",
    program_interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Inquiry sent. Coach Bledsoe will be in touch.");
      setForm({
        name: "",
        email: "",
        phone: "",
        player_age: "",
        program_interest: "",
        message: "",
      });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={CONTACT.section}
      className="section-pad bg-[#050B14] relative"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="overline">Get In Touch</div>
          <div className="gold-divider mt-4 mb-6" />
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
            Ready for your next{" "}
            <span className="text-[#B8C5D6]">basketball opportunity?</span>
          </h2>
          <p className="mt-6 text-[#A0ABC0] leading-relaxed max-w-md">
            Tell us about your player. Coach Bledsoe and the BBA team will
            reach out with the right path forward — camps, training, or
            travel team tryouts.
          </p>

          <div className="mt-10 space-y-5">
            <a
              href={`mailto:${EMAIL}`}
              data-testid={CONTACT.emailLink}
              className="flex items-center gap-4 group"
            >
              <div className="w-11 h-11 grid place-items-center border border-white/15 group-hover:border-[#B8C5D6] transition-colors">
                <Mail size={18} className="text-[#B8C5D6]" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-white/50">
                  Email
                </div>
                <div className="text-white group-hover:text-[#B8C5D6] transition-colors">
                  {EMAIL}
                </div>
              </div>
            </a>

            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noreferrer noopener"
              data-testid={CONTACT.instagramLink}
              className="flex items-center gap-4 group"
            >
              <div className="w-11 h-11 grid place-items-center border border-white/15 group-hover:border-[#B8C5D6] transition-colors">
                <Instagram size={18} className="text-[#B8C5D6]" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-white/50">
                  Instagram
                </div>
                <div className="text-white group-hover:text-[#B8C5D6] transition-colors">
                  @bbatravelbasketball
                </div>
              </div>
            </a>

            <div className="flex items-center gap-4">
              <div className="w-11 h-11 grid place-items-center border border-white/15">
                <MapPin size={18} className="text-[#B8C5D6]" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-white/50">
                  Location
                </div>
                <div className="text-white">Lees Summit, MO</div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {submitted ? (
            <div
              data-testid={CONTACT.successMsg}
              className="bba-card p-10 sm:p-12 text-center"
            >
              <div className="w-14 h-14 mx-auto grid place-items-center border border-[#B8C5D6] text-[#B8C5D6] mb-6">
                <CheckCircle2 size={26} />
              </div>
              <h3 className="font-display text-2xl sm:text-3xl">
                Inquiry received.
              </h3>
              <p className="text-[#A0ABC0] mt-4 max-w-md mx-auto">
                Thanks for reaching out to Bledsoe Basketball Academy. A
                coach will follow up shortly. For urgent matters, email{" "}
                <a
                  href={`mailto:${EMAIL}`}
                  className="text-[#B8C5D6] underline underline-offset-4"
                >
                  {EMAIL}
                </a>
                .
              </p>
              <button
                type="button"
                onClick={() => setSubmitted(false)}
                className="mt-8 btn-ghost-gold px-6 py-3 text-sm uppercase"
              >
                Send Another
              </button>
            </div>
          ) : (
            <form
              data-testid={CONTACT.form}
              onSubmit={onSubmit}
              className="bba-card p-7 sm:p-10 space-y-5"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-[11px] tracking-[0.22em] uppercase text-white/60">
                    Name *
                  </label>
                  <input
                    type="text"
                    required
                    data-testid={CONTACT.inputName}
                    value={form.name}
                    onChange={onChange("name")}
                    placeholder="Parent or player name"
                    className="bba-input w-full mt-2 px-4 py-3 text-sm"
                  />
                </div>
                <div>
                  <label className="text-[11px] tracking-[0.22em] uppercase text-white/60">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    data-testid={CONTACT.inputEmail}
                    value={form.email}
                    onChange={onChange("email")}
                    placeholder="you@email.com"
                    className="bba-input w-full mt-2 px-4 py-3 text-sm"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-[11px] tracking-[0.22em] uppercase text-white/60">
                    Phone
                  </label>
                  <input
                    type="tel"
                    data-testid={CONTACT.inputPhone}
                    value={form.phone}
                    onChange={onChange("phone")}
                    placeholder="(555) 555-5555"
                    className="bba-input w-full mt-2 px-4 py-3 text-sm"
                  />
                </div>
                <div>
                  <label className="text-[11px] tracking-[0.22em] uppercase text-white/60">
                    Player Age / Grade
                  </label>
                  <input
                    type="text"
                    data-testid={CONTACT.inputAge}
                    value={form.player_age}
                    onChange={onChange("player_age")}
                    placeholder="e.g. 8th Grade"
                    className="bba-input w-full mt-2 px-4 py-3 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-white/60">
                  Program of Interest
                </label>
                <select
                  data-testid={CONTACT.selectProgram}
                  value={form.program_interest}
                  onChange={onChange("program_interest")}
                  className="bba-input w-full mt-2 px-4 py-3 text-sm appearance-none"
                >
                  <option value="">Select a program</option>
                  {programOptions.map((opt) => (
                    <option key={opt} value={opt} className="bg-[#0A1128]">
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-white/60">
                  Message *
                </label>
                <textarea
                  required
                  data-testid={CONTACT.inputMessage}
                  value={form.message}
                  onChange={onChange("message")}
                  rows={5}
                  placeholder="Tell us about your player and goals..."
                  className="bba-input w-full mt-2 px-4 py-3 text-sm resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid={CONTACT.submit}
                className="btn-gold inline-flex items-center gap-2 px-7 py-4 text-sm uppercase w-full sm:w-auto justify-center disabled:opacity-60"
              >
                {submitting ? "Sending..." : "Send Inquiry"}
                <Send size={16} />
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
